import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, categoryVideos, type Video } from '@/mocks/videos';

function formatViews(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  return n.toString();
}

function VideoCard({ video }: { video: Video }) {
  return (
    <div className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-200">
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          title={`紅桃視頻 - ${video.title}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-1.5 py-0.5 rounded">
          {video.duration}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 cursor-pointer">
            <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 leading-snug mb-2">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full overflow-hidden">
              <img src={video.authorAvatar} alt={video.author} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <span>{video.author}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line text-xs"></i>
              {formatViews(video.views)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const videos = slug ? (categoryVideos[slug] || []) : [];

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类不存在</h1>
            <p className="text-foreground-500 mb-6">请检查分类链接是否正确</p>
            <Link
              to="/"
              className="whitespace-nowrap px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} - 紅桃視頻分類頻道`}
        description={`紅桃視頻${category.name}分類頻道，精選${category.count.toLocaleString()}個${category.name}相關短視頻內容，高清流暢播放，免費觀看。`}
        keywords={`紅桃視頻,${category.name},${category.name}短視頻,紅桃視頻${category.name}頻道`}
        canonicalUrl={`https://hongtao.video/category/${slug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `紅桃視頻 - ${category.name}分類`,
          description: `紅桃視頻${category.name}分類頻道，精選${category.count.toLocaleString()}個相關短視頻內容。`,
          url: `https://hongtao.video/category/${slug}`,
          isPartOf: { '@type': 'WebSite', name: '紅桃視頻', url: 'https://hongtao.video' },
        }}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类' },
              { label: category.name },
            ]}
          />

          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-14 h-14 flex items-center justify-center rounded-lg ${colorMap[category.color]}`}>
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {category.name}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">
                    共 {category.count.toLocaleString()} 个视频
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      c.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-200/70 text-foreground-600 hover:bg-background-300'
                    }`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>

              {videos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {videos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200/70">
                    <i className="ri-video-line text-foreground-400 text-3xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无视频内容</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}